import { Component, Prop } from 'vue-property-decorator'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import GridHeader from '@/components/GridHeader.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import ScientificAnalysisSummary from '../scientific-analysis-summary/scientific-analysis-summary.vue'
import { ScientificAnalysis } from '@/modules/scientific-analysis-module/shared/scientific-analysis.models'
import {
    initialScientificAnalysisResultsPanelState,
    ScientificAnalysisResultsPanelState
} from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/scientific-analysis-results-panel.models'
import { ColDef, ColGroupDef, GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { scientificAnalysisDatasourceFactory } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource-factory'
import {
    ScientificAnalysisDatasource,
    ScientificAnalysisPatientNameDatasource
} from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource'
import patientResource from '@/api/resources/PatientResource'
import commonCaseServices from '@/api/resources/common/commonCaseServices'
import { registryCasePrintService } from '@/modules/printModule/services/registry-case-print-service'
import { scientificAnalysisService } from '@/modules/scientific-analysis-module/services/scientific-analysis-services'
import { CommonFilterModel, CommonExportType } from '@/api/resources/common/shared/models/common.models'
import { AgGridCallbackEvent } from '@/shared/models/ag-grid.model'
import { encoder } from '@/utils/encoder'
import { GridUtils } from '@/shared/util/grid-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { cloneDeep, uniqueId } from 'lodash'
import grossingSampleService from '@/modules/grossingModule/services/grossingSampleService'
import BaseCrudInfiniteServerSideComponent from '@/shared/resources/base-crud-infinite-side.component'
import { CaseSummaryReportGroupingTypeEnum, ScientificAnalysisEntityResultType, ScientificAnalysisSearchEntityType } from '@/modules/scientific-analysis-module/services/scientific-analysis-dtos'
import { AxiosResponse } from 'axios'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        VtGrid,
        GridHeader,
        GridDisplayOptions,
        ScientificAnalysisSummary
    }
})
export default class ScientificAnalysisResultsPanel extends BaseCrudInfiniteServerSideComponent<ScientificAnalysisEntityResultType> {
    @Prop() scientificAnalysis!: ScientificAnalysis
    saState: ScientificAnalysisResultsPanelState
    commonCaseServices = commonCaseServices
    registryCasePrintService = registryCasePrintService

    constructor() {
        super()
        this.saState = initialScientificAnalysisResultsPanelState()
    }

    public search(): void {
        this.state.gridApi?.deselectAll()
        const oldDatasourceService = this.state.datasource.service
        this.state.gridApi?.setColumnDefs([])
        this.state.datasource.service = scientificAnalysisDatasourceFactory.getDatasource(this.scientificAnalysis)
        if (oldDatasourceService !== this.state.datasource.service) {
            this.saState.columnDefs = null
        }
        this.state.totalElements = null
        this.state.updateGrid = uniqueId()
        let pageSize = this.gridOptions.paginationPageSize
        this.changedPageSize((pageSize ?? 50))

        this.state.gridApi?.paginationGoToFirstPage()
        this.state.columnApi?.applyColumnState({
            defaultState: { sort: null }
        })

        this.saState.dirty = false
        this.saState.scientificAnalysisUsed = cloneDeep(this.scientificAnalysis)
    }

    async created(): Promise<void> {
        await grossingSampleService.ensureSampleStatesAreLoaded()
    }

    mounted(): void {
        this.search()
    }

    getRowHeight(data: ScientificAnalysisEntityResultType): number {
        const dataSource = (this.state.datasource.service as ScientificAnalysisDatasource<ScientificAnalysisEntityResultType>)
        return dataSource.getRowHeight(data)
    }

    get gridTitle(): string {
        let title = `${this.$tc('scientificAnalysis.grid.title')}`
        if (this.state.totalElements) {
            title = `${title} (${this.state.totalElements.toLocaleString()})`
        }
        return title
    }

    get visibleAPAReport(): boolean {
        return this.scientificAnalysis.searchedEntityType !== ScientificAnalysisSearchEntityType.PATIENT
    }

    async _onPathologicalReportClick(): Promise<void> {
        await this.showPathologicalReport(this.state.selectedRows[0])
    }

    private async showPathologicalReport(selectedRow: ScientificAnalysisEntityResultType): Promise<void> {
        const [labId, caseId] = (this.state.datasource.service as ScientificAnalysisDatasource<ScientificAnalysisEntityResultType>).getLabAndCaseId(selectedRow)
        if (caseId && labId) {
            let includeAssociated = false
            if (await this.hasAssociatedCases(labId, caseId)) {
                includeAssociated = await this.$_showConfirmationModal(this.$t('global.modal.case.viewAssociatedCasesConfirmation') as string)
            }
            const blob = await this.$_toCallUnique(this.registryCasePrintService.downloadCaseAPReportPdf(labId, caseId, includeAssociated))
            if (blob) {
                this.$_openFile({ blob })
            }
        }
    }

    private async hasAssociatedCases(labId:number, caseId:number):Promise<boolean> {
        const response =
            await PromiseUtils.toCallUnique(
                this.commonCaseServices.getAssociatedCases(labId, caseId))
        if (response !== null) {
            return response?.length && response.length > 0
        }
        return false
    }

    get _disablePathologicalReportButton():boolean {
        return this._disablePatientPreviousCasesButton
    }

    get _showSummaryReportButton(): boolean {
        return this.scientificAnalysis.searchedEntityType === ScientificAnalysisSearchEntityType.CASE
    }

    get _disablePatientPreviousCasesButton():boolean {
        return this.state.selectedRows.length !== 1
    }

    async _onExportPatientPreviousCasesReportPDFClick():Promise<void> {
        await this._exportPatientPreviousCasesReportPDF(this.state.selectedRows[0])
    }

    private async _exportPatientPreviousCasesReportPDF(selectedRow: ScientificAnalysisEntityResultType):Promise<void> {
        const [patId, labId] = (this.state.datasource.service as ScientificAnalysisDatasource<ScientificAnalysisEntityResultType>).getPatAndLabId(selectedRow)
        const blob = await this.$_toCallUnique(patientResource.getPreviousCaseAPReport(labId, patId, false)) as StrictNullable<Blob>
        if (blob) {
            this.$_openFile({ blob })
        }
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        if (!params?.node) {
            return []
        } else {
            return [
                {
                    name: this.$tc('global.pathologicalReport'),
                    cssClasses: this.state.selectedRows.length > 1 || !this.visibleAPAReport ? ['not-show'] : [''],
                    action: () => this.showPathologicalReport(params?.node?.data)
                },
                {
                    name: this.$tc('scientificAnalysis.grid.patientCasesReport'),
                    cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                    action: () => this._exportPatientPreviousCasesReportPDF(params?.node?.data)
                }
            ]
        }
    }

    _toggleSummarySidePanel():void {
        this.saState.toggleSummarySidepanel = !this.saState.toggleSummarySidepanel
    }

    async _onSummaryReportByPathologist(): Promise<void> {
        await this._onSummaryReportBy(CaseSummaryReportGroupingTypeEnum.PATHOLOGIST)
    }

    async _onSummaryReportByPatient(): Promise<void> {
        await this._onSummaryReportBy(CaseSummaryReportGroupingTypeEnum.PATIENT)
    }

    async _onSummaryReportBy(entityGroup: CaseSummaryReportGroupingTypeEnum): Promise<void> {
        const blob =
            await this.$_toCallSecureLoading(
                scientificAnalysisService.summaryReport(
                    entityGroup,
                    this.scientificAnalysis,
                    this.state.selectedRows.map(selectedRow => selectedRow.id)))
        if (blob) {
            this.$_openFile({ blob })
        }
    }

    exportReport(exportType: CommonExportType): Promise<AxiosResponse<Blob>> | null {
        const columns = this.getColumnsToExport()
        if (columns && this.state.gridApi) {
            const filterModel: CommonFilterModel = encoder.parseBooleans(this.state.gridApi.getFilterModel())
            const sortModel = GridUtils.getSortValue(this.state.gridApi.getSortModel())
            return scientificAnalysisService.exportTo(
                exportType,
                this.scientificAnalysis,
                this.$tc('scientificAnalysis.grid.title'),
                filterModel,
                sortModel,
                columns,
                this.state.selectedRows.map(selectedRow => selectedRow.id))
        }
        return null
    }

    get _columnDefs(): (ColDef | ColGroupDef)[] {
        if (!this.saState.columnDefs) {
            if (this.state.datasource.service != null) {
                const dataSource = (this.state.datasource.service as ScientificAnalysisDatasource<ScientificAnalysisEntityResultType>)
                if (dataSource as unknown instanceof ScientificAnalysisPatientNameDatasource) {
                    const nameDatasource = dataSource as ScientificAnalysisPatientNameDatasource<ScientificAnalysisEntityResultType>

                    const [middleNameColumnName, surnameColumnName] = nameDatasource.getPatientColumns()
                    this.saState.columnDefs = this.$_filterPatientColumns(
                        dataSource.getGridColumns(),
                        middleNameColumnName,
                        surnameColumnName
                    )
                } else {
                    this.saState.columnDefs = dataSource.getGridColumns()
                }
            } else {
                return []
            }
        }
        return this.saState.columnDefs
    }

    onEndCallback(_event: AgGridCallbackEvent) {
        const dataSource = (this.state.datasource.service as ScientificAnalysisDatasource<ScientificAnalysisEntityResultType>)
        this.state.totalElements = dataSource.getTotalElements()
    }

    public setDirty():void {
        this.saState.dirty = true
    }
}
